import { FileItem } from '../api/client';
import { formatBytes, formatDate } from '../lib/format';
import { Download, Link2, MoreVertical, Trash2 } from 'lucide-react';
import { clsx } from 'clsx';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';

interface FileCardProps {
  file: FileItem;
  onShare: (fileId: string) => void;
  onDelete: (fileId: string) => void;
}

const iconMap: Record<string, string> = {
  'image/': '🖼️',
  'video/': '🎬',
  'audio/': '🎧',
  'application/pdf': '📄',
};

function pickIcon(mime?: string | null) {
  if (!mime) return '📦';
  const entry = Object.entries(iconMap).find(([key]) => mime.startsWith(key));
  return entry ? entry[1] : '📦';
}

export function FileCard({ file, onShare, onDelete }: FileCardProps) {
  const { token } = useAuth();

  const handleDownload = async () => {
    try {
      const response = await fetch(`/api/files/${file.id}/download`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error('下载失败');
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = file.name;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      console.error('下载失败:', error);
      toast.error('下载失败');
    }
  };

  return (
    <div className="group glass-panel flex flex-col gap-4 rounded-3xl p-4 text-white">
      <div className="flex items-center justify-between">
        <span className="text-4xl">{pickIcon(file.mimeType)}</span>
        <button className="rounded-full border border-white/10 p-2 opacity-0 transition group-hover:opacity-100">
          <MoreVertical className="h-4 w-4 text-white/60" />
        </button>
      </div>
      <div>
        <p className="truncate text-lg font-semibold">{file.name}</p>
        <p className="text-xs text-white/50">{formatBytes(file.size)}</p>
        <p className="text-xs text-white/40">{formatDate(file.updatedAt)}</p>
      </div>
      <div className="mt-auto flex items-center gap-2 text-xs text-white/60">
        <button
          className={clsx('flex flex-1 items-center justify-center gap-1 rounded-full border border-white/10 px-3 py-2')}
          onClick={handleDownload}
        >
          <Download className="h-3 w-3" /> 下载
        </button>
        <button
          className="flex flex-1 items-center justify-center gap-1 rounded-full border border-white/10 px-3 py-2"
          onClick={() => onShare(file.id)}
        >
          <Link2 className="h-3 w-3" /> 分享
        </button>
        <button
          className="flex items-center justify-center rounded-full border border-red-500/30 px-3 py-2 text-red-300"
          onClick={() => onDelete(file.id)}
        >
          <Trash2 className="h-3 w-3" />
        </button>
      </div>
    </div>
  );
}
